"use strict";

import _defineProperty from "/app/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _toConsumableArray from "/app/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _slicedToArray from "/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import React, { useCallback, useContext, useLayoutEffect, useRef, useState } from "react";
import { Transition } from "react-transition-group";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import classNames from "classnames";
import { ChevronLeft, ChevronRight, IconSize } from "../../../global/icons";
import { AnimationDuration } from "../../../styles/Animations";
import { ButtonType, ButtonSize } from "../Button/Button";
import IconButton from "../IconButton/IconButton";
import * as styles from "./Carousel-css";
import { spacing } from "../../../tokens/tokens-css";
import { randomString } from "../../../utils";
export var Carousel = function Carousel(_ref) {
  var _assignInlineVars;
  var _ref$itemsPerSlide = _ref.itemsPerSlide,
    itemsPerSlide = _ref$itemsPerSlide === void 0 ? 1 : _ref$itemsPerSlide,
    _ref$buttonsAlwaysSho = _ref.buttonsAlwaysShow,
    buttonsAlwaysShow = _ref$buttonsAlwaysSho === void 0 ? true : _ref$buttonsAlwaysSho,
    className = _ref.className,
    children = _ref.children;
  var listRef = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasScrolledForward = _useState2[0],
    setHasScrolledForward = _useState2[1];
  var _useState3 = useState(true),
    _useState4 = _slicedToArray(_useState3, 2),
    hasMoreToScrollForward = _useState4[0],
    setHasMoreToScrollForward = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isHovered = _useState6[0],
    setIsHovered = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isFocused = _useState8[0],
    setIsFocused = _useState8[1];
  var _useState9 = useState(0),
    _useState10 = _slicedToArray(_useState9, 2),
    itemCount = _useState10[0],
    setItemCount = _useState10[1];
  var items = useRef([]);
  var registerItem = useCallback(function (id) {
    items.current = [].concat(_toConsumableArray(items.current), [id]);
    setItemCount(items.current.length);
  }, []);
  var unregisterItem = useCallback(function (id) {
    items.current = items.current.filter(function (item) {
      return item !== id;
    });
    setItemCount(items.current.length);
  }, []);
  var showButtons = buttonsAlwaysShow || isHovered || isFocused;
  var listPadding = spacing.xs * 2;
  var onScroll = function onScroll() {
    if (!listRef.current) return;
    var scrollAmount = listRef.current.scrollLeft;
    var scrollSize = listRef.current.scrollWidth;
    var listWidth = listRef.current.clientWidth - listPadding;
    setHasScrolledForward(listRef.current.scrollLeft > listPadding);
    setHasMoreToScrollForward(scrollSize - scrollAmount - listWidth > listPadding);
  };
  var scrollList = function scrollList(direction) {
    if (!listRef.current) return;
    var distance = listRef.current.clientWidth - listPadding;
    listRef.current.scrollBy(distance * direction, 0);
  };
  return /* @__PURE__ */React.createElement("div", {
    className: classNames(styles.carousel, className),
    onMouseEnter: function onMouseEnter() {
      setIsHovered(true);
    },
    onMouseLeave: function onMouseLeave() {
      setIsHovered(false);
    }
  }, /* @__PURE__ */React.createElement("div", {
    className: styles.slider,
    style: assignInlineVars((_assignInlineVars = {}, _defineProperty(_assignInlineVars, styles.itemCount, itemCount.toString()), _defineProperty(_assignInlineVars, styles.itemSize, "".concat(1 / itemsPerSlide * 100, "%")), _assignInlineVars)),
    ref: listRef,
    onScroll: onScroll,
    onFocus: function onFocus() {
      setIsFocused(true);
    },
    onBlur: function onBlur() {
      setIsFocused(false);
    }
  }, /* @__PURE__ */React.createElement(carouselContext.Provider, {
    value: {
      registerItem: registerItem,
      unregisterItem: unregisterItem
    }
  }, children)), /* @__PURE__ */React.createElement(Transition, {
    in: hasScrolledForward,
    mountOnEnter: true,
    unmountOnExit: true,
    timeout: AnimationDuration.Short
  }, function (status) {
    return /* @__PURE__ */React.createElement("div", {
      className: styles.buttonContainer,
      onFocus: function onFocus() {
        setIsFocused(true);
      },
      onBlur: function onBlur() {
        setIsFocused(false);
      }
    }, /* @__PURE__ */React.createElement(IconButton, {
      className: styles.sliderButton({
        location: "left",
        status: status,
        visible: showButtons && hasScrolledForward
      }),
      icon: /* @__PURE__ */React.createElement(ChevronLeft, {
        size: IconSize.Small
      }),
      type: ButtonType.Tertiary,
      size: ButtonSize.XSmall,
      "aria-label": "previous slide",
      onClick: function onClick() {
        scrollList(-1);
      },
      isIconOnly: true
    }));
  }), /* @__PURE__ */React.createElement(Transition, {
    in: hasMoreToScrollForward,
    mountOnEnter: true,
    unmountOnExit: true,
    timeout: AnimationDuration.Short
  }, function (status) {
    return /* @__PURE__ */React.createElement("div", {
      className: styles.buttonContainer,
      onFocus: function onFocus() {
        setIsFocused(true);
      },
      onBlur: function onBlur() {
        setIsFocused(false);
      }
    }, /* @__PURE__ */React.createElement(IconButton, {
      className: styles.sliderButton({
        location: "right",
        status: status,
        visible: showButtons && hasMoreToScrollForward
      }),
      "aria-label": "next slide",
      icon: /* @__PURE__ */React.createElement(ChevronRight, {
        size: IconSize.Small
      }),
      type: ButtonType.Tertiary,
      size: ButtonSize.XSmall,
      onClick: function onClick() {
        scrollList(1);
      },
      isIconOnly: true
    }));
  }));
};
export var CarouselItem = function CarouselItem(_ref2) {
  var children = _ref2.children,
    className = _ref2.className;
  var _useCarouselContext = useCarouselContext(),
    registerItem = _useCarouselContext.registerItem,
    unregisterItem = _useCarouselContext.unregisterItem;
  var itemId = useRef(randomString());
  useLayoutEffect(function () {
    var id = itemId.current;
    registerItem(id);
    return function () {
      unregisterItem(id);
    };
  }, [registerItem, unregisterItem]);
  return /* @__PURE__ */React.createElement("div", {
    className: classNames(styles.item, className)
  }, children);
};
var carouselContext = React.createContext(void 0);
var useCarouselContext = function useCarouselContext() {
  var context = useContext(carouselContext);
  if (!context) {
    throw new Error("useCarouselContext must be used inside a CarouselContext.Provider");
  }
  return context;
};